import type { ActionLabel, ConfidentialityLevel, Discussion, DiscussionType, JurisdictionTag, LexData, MembershipCategory, PostType, TopicTag, User, WorkflowStatus } from "./types";

export const postTypes: PostType[] = ["Question", "Discussion", "Proposal", "Amendment", "Resource", "Update", "Event", "Consultation", "Poll", "Vote", "Task", "Announcement"];
export const topicTags: TopicTag[] = [
  "Data protection",
  "AI regulation",
  "Digital assets",
  "Financial regulation",
  "Payments",
  "AML/CFT",
  "Intellectual property",
  "Open-source software",
  "Cybersecurity",
  "DORA",
  "Platform regulation",
  "Consumer law",
  "Competition",
  "Corporate governance",
  "Litigation",
  "Arbitration",
  "Employment",
  "Tax",
  "Public affairs",
  "Legal operations",
  "EU affairs",
  "Working group",
  "Member governance",
  "Referral/cooperation",
  "Events",
  "Knowledge sharing",
];
export const jurisdictionTags: JurisdictionTag[] = ["EU", "Luxembourg", "Belgium", "France", "Germany", "Netherlands", "Italy", "Spain", "United Kingdom", "Switzerland", "United States", "Cross-border", "Global"];
export const confidentialityLevels: ConfidentialityLevel[] = ["Network-wide", "Working group only", "Full Members only", "Committee only", "Invited participants only", "Public/exportable"];
export const actionLabels: ActionLabel[] = ["Input requested", "Vote required", "Review requested", "Decision needed", "Volunteer requested", "Deadline approaching", "Waiting for proposer", "Waiting for committee", "No action required"];
export const workflowStatusesByPostType: Record<PostType, WorkflowStatus[]> = {
  Question: ["Draft", "Open", "Input requested", "Resolved", "Archived"],
  Discussion: ["Draft", "Open", "Under review", "Resolved", "Archived"],
  Proposal: ["Draft", "Submitted", "Under review", "Open", "Amendment window", "Ready for vote", "Voting open", "Voting closed", "Adopted", "Rejected", "Withdrawn", "Archived"],
  Amendment: ["Draft", "Submitted", "Under review", "Voting open", "Voting closed", "Adopted", "Rejected", "Withdrawn", "Archived"],
  Resource: ["Draft", "Open", "Under review", "Archived"],
  Update: ["Draft", "Open", "Archived"],
  Event: ["Draft", "Open", "Closed", "Archived"],
  Consultation: ["Draft", "Open", "Input requested", "Under review", "Resolved", "Archived"],
  Poll: ["Draft", "Open", "Closed", "Archived"],
  Vote: ["Draft", "Voting open", "Voting closed", "Certified", "Archived"],
  Task: ["Draft", "Open", "Under review", "Resolved", "Archived"],
  Announcement: ["Draft", "Open", "Archived"],
};

export type TagSuggestionStatus = "suggested" | "approved" | "archived";

export interface PostDraft {
  postType?: PostType | PostType[];
  title?: string;
  body?: string;
  summary?: string;
  fullText?: string;
  linkedProposalId?: string | null;
  linkedDecisionId?: string | null;
  confidentialityLevel?: ConfidentialityLevel;
  topicTags?: string[];
  jurisdictionTags?: JurisdictionTag[];
  actionLabels?: ActionLabel[];
  deadline?: number | null;
}

export interface PostFilters {
  postType?: PostType | "All" | "My actions";
  topicTag?: TopicTag | "All";
  jurisdictionTag?: JurisdictionTag | "All";
  confidentialityLevel?: ConfidentialityLevel | "All";
  workflowStatus?: WorkflowStatus | "All";
  actionLabel?: ActionLabel | "All";
  authorFirmId?: string;
}

export function postTypeFromDiscussionType(type: DiscussionType): PostType {
  if (type === "question") return "Question";
  if (type === "governance_proposal") return "Proposal";
  if (type === "joint_position" || type === "memorandum" || type === "practice_note") return "Resource";
  if (type === "legislative_comment") return "Consultation";
  return "Discussion";
}

export function normalizePost(discussion: Discussion, data?: LexData) {
  const mappedTopics = data ? data.practiceAreas.filter((area) => discussion.practiceAreaIds.includes(area.id)).map((area) => practiceAreaToTopicTag(area.name)) : [];
  const postType = discussion.postType ?? postTypeFromDiscussionType(discussion.type);
  return {
    postType,
    topicTags: discussion.topicTags?.length ? discussion.topicTags : mappedTopics,
    jurisdictionTags: discussion.jurisdictionTags as JurisdictionTag[],
    confidentialityLevel: discussion.confidentialityLevel ?? confidentialityFromVisibility(discussion.visibility),
    workflowStatus: discussion.workflowStatus ?? workflowFromLegacyStatus(discussion.status, postType),
    actionLabels: discussion.actionLabels?.length ? discussion.actionLabels : deriveActionLabels({ ...discussion, postType }),
    relatedLinks: discussion.relatedLinks ?? [],
    deadline: discussion.deadline ?? null,
  };
}

export function validatePostDraft(draft: PostDraft) {
  const errors: string[] = [];
  if (!draft.postType) errors.push("Post type is required.");
  if (Array.isArray(draft.postType)) errors.push("Post must have exactly one primary post type.");
  if (!draft.confidentialityLevel) errors.push("Confidentiality level is required.");
  const postType = Array.isArray(draft.postType) ? draft.postType[0] : draft.postType;
  if (postType === "Question" && !draft.body?.trim()) errors.push("Question requires a question body.");
  if (postType === "Proposal" && (!draft.summary?.trim() || !draft.fullText?.trim())) errors.push("Proposal requires summary and full proposal text.");
  if (postType === "Amendment" && !draft.linkedProposalId) errors.push("Amendment must be linked to a proposal.");
  if (postType === "Vote" && !draft.linkedProposalId && !draft.linkedDecisionId) errors.push("Vote must be linked to a proposal or formal decision.");
  return errors;
}

export function filterPosts(discussions: Discussion[], filters: PostFilters, data: LexData) {
  return discussions.filter((discussion) => {
    const classification = normalizePost(discussion, data);
    if (filters.postType && filters.postType !== "All" && filters.postType !== "My actions" && classification.postType !== filters.postType) return false;
    if (filters.postType === "My actions" && !classification.actionLabels.some((label) => label !== "No action required")) return false;
    if (filters.topicTag && filters.topicTag !== "All" && !classification.topicTags.includes(filters.topicTag)) return false;
    if (filters.jurisdictionTag && filters.jurisdictionTag !== "All" && !classification.jurisdictionTags.includes(filters.jurisdictionTag)) return false;
    if (filters.confidentialityLevel && filters.confidentialityLevel !== "All" && classification.confidentialityLevel !== filters.confidentialityLevel) return false;
    if (filters.workflowStatus && filters.workflowStatus !== "All" && classification.workflowStatus !== filters.workflowStatus) return false;
    if (filters.actionLabel && filters.actionLabel !== "All" && !classification.actionLabels.includes(filters.actionLabel)) return false;
    if (filters.authorFirmId && discussion.createdByFirmId !== filters.authorFirmId) return false;
    return true;
  });
}

export function canCreatePostType(input: { membershipCategory: MembershipCategory; postType: PostType; invited?: boolean }) {
  const full: PostType[] = ["Question", "Discussion", "Proposal", "Resource", "Update", "Event", "Consultation", "Poll", "Task", "Amendment", "Vote"];
  const associate: PostType[] = ["Question", "Discussion", "Resource", "Consultation", "Poll", "Amendment"];
  const expert: PostType[] = ["Resource", "Update", "Consultation", "Poll"];
  if (input.membershipCategory === "Full Member") return full.includes(input.postType);
  if (input.membershipCategory === "Associate Member") return associate.includes(input.postType);
  if (input.membershipCategory === "Expert Contributor") return expert.includes(input.postType) || Boolean(input.invited && input.postType === "Discussion");
  if (input.membershipCategory === "Observer") return Boolean(input.invited);
  return false;
}

export function canViewPostByConfidentiality(input: { user: User; viewerMembershipCategory: MembershipCategory; discussion: Discussion; isCommittee?: boolean; isAdmin?: boolean; workingGroupIds?: string[] }) {
  const level = normalizePost(input.discussion).confidentialityLevel;
  if (level === "Network-wide" || level === "Public/exportable") return true;
  if (input.isAdmin || input.isCommittee) return true;
  if (level === "Full Members only") return input.viewerMembershipCategory === "Full Member" || input.discussion.invitedUserIds?.includes(input.user.id) === true;
  if (level === "Invited participants only") return input.discussion.invitedUserIds?.includes(input.user.id) === true;
  if (level === "Working group only") return Boolean(input.discussion.workingGroupId && input.workingGroupIds?.includes(input.discussion.workingGroupId));
  return false;
}

export function suggestedTagRequiresApproval(status: TagSuggestionStatus) {
  return status !== "approved";
}

export function isAdvisoryPost(postType: PostType) {
  return postType === "Poll";
}

function practiceAreaToTopicTag(name: string): TopicTag {
  const matches: Partial<Record<string, TopicTag>> = {
    "Data protection": "Data protection",
    Competition: "Competition",
    Employment: "Employment",
    Tax: "Tax",
    Regulatory: "Platform regulation",
    Corporate: "Corporate governance",
    "Commercial litigation": "Litigation",
    "Public law": "Public affairs",
  };
  return matches[name] ?? "Knowledge sharing";
}

function confidentialityFromVisibility(visibility: Discussion["visibility"]): ConfidentialityLevel {
  if (visibility === "committee" || visibility === "admin") return "Committee only";
  if (visibility === "firm") return "Invited participants only";
  return "Network-wide";
}

function workflowFromLegacyStatus(status: Discussion["status"], postType: PostType): WorkflowStatus {
  if (status === "drafting") return postType === "Proposal" ? "Amendment window" : "Under review";
  if (status === "voting") return "Voting open";
  if (status === "closed") return "Archived";
  return postType === "Question" || postType === "Consultation" ? "Input requested" : "Open";
}

function deriveActionLabels(discussion: Discussion & { postType: PostType }): ActionLabel[] {
  if (discussion.postType === "Vote" || discussion.status === "voting") return ["Vote required"];
  if (discussion.postType === "Question" || discussion.postType === "Consultation") return ["Input requested"];
  if (discussion.postType === "Proposal") return ["Review requested"];
  if (discussion.deadline && discussion.deadline - Date.now() < 7 * 24 * 60 * 60 * 1000) return ["Deadline approaching"];
  return ["No action required"];
}
